import { z } from 'zod';
import { jobStatusSchema, jobTypeSchema } from './enums.js';
/**
 * DTO d'un job asynchrone exposé par l'API (centre de jobs, XPHOTO-17).
 * Aligné sur la table `jobs` d'ADR-0002 (§D).
 *
 * `total`/`done` pilotent la barre de progression ; `etaSeconds` l'ETA affiché ;
 * `dryRun` indique une simulation (opérations de masse). `params`/`error` sont
 * libres (jsonb / texte) et peuvent être nuls.
 *
 * `userId`/`userEmail` (Lot A) tracent QUI a lancé le job (renseignés par la route
 * d'enfilement) — le contexte de job en a besoin pour attribuer les erreurs de
 * traitement de fichiers. `result` (Lot A) porte la sortie structurée du handler
 * persistée à la finalisation (p.ex. les `ScanCounts` d'un scan).
 */
export const jobSchema = z.object({
    id: z.uuid(),
    libraryId: z.uuid(),
    type: jobTypeSchema,
    status: jobStatusSchema,
    total: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    etaSeconds: z.number().int().nonnegative().nullable(),
    dryRun: z.boolean(),
    params: z.record(z.string(), z.unknown()).nullable(),
    error: z.string().nullable(),
    /**
     * Utilisateur ayant lancé le job (ou `null` : bootstrap/planificateur).
     * OPTIONNEL dans le contrat (champ additif Lot A) : les consommateurs existants
     * qui n'en tiennent pas compte restent valides ; l'API le renvoie toujours.
     */
    userId: z.uuid().nullable().optional(),
    /** E-mail de l'utilisateur, en clair, ou `null` (additif, optionnel). */
    userEmail: z.string().nullable().optional(),
    /** Sortie structurée du handler (ex. ScanCounts) persistée à la fin (additif, optionnel). */
    result: z.record(z.string(), z.unknown()).nullable().optional(),
    createdAt: z.string().datetime({ offset: true }),
    startedAt: z.string().datetime({ offset: true }).nullable(),
    finishedAt: z.string().datetime({ offset: true }).nullable(),
});
/**
 * Types de job qu'on accepte de RELANCER (`POST /api/jobs/:id/retry`, audit B3).
 *
 * Le besoin est réel : `failOrphanedRunning` passe tout job `running` en `failed` au
 * redémarrage, donc un scan de plusieurs heures interrompu par un `docker restart` est
 * perdu sans reprise. Mais « relancer » ne peut PAS être offert sur n'importe quoi — un
 * job en échec laisse la bibliothèque dans un état PARTIELLEMENT appliqué que ses
 * `params` d'origine ne décrivent plus.
 *
 * RÈGLE RETENUE — un type est relançable seulement s'il est à la fois :
 *   1. IDEMPOTENT : le rejouer sur un état déjà (partiellement) traité converge vers le
 *      même résultat, au lieu de composer un second effet ;
 *   2. NON DESTRUCTIF sur `/library` : il n'efface ni ne réécrit aucun fichier original.
 *
 * ADMIS (les trois seuls) :
 *  - `scan`    : réconciliation disque↔index, idempotente par construction — et c'est
 *                le cas qui MOTIVE ce constat ;
 *  - `dedup`   : clustering pHash, pure analyse (n'écrit que ses propres clusters) ;
 *  - `ai_tag`  : propositions de tags, upsert en base, aucune écriture disque.
 *
 * REFUSÉS, et POURQUOI (chacun casse au moins une des deux conditions) :
 *  - `rotate`             : la rotation est RELATIVE (composée sur l'orientation
 *                           courante, cf. `rotate.ts`) — rejouer un quart de tour en
 *                           fait un demi-tour. Non idempotent, dégât silencieux.
 *  - `trash_purge`        : avec `all: true`, la cible est résolue À L'EXÉCUTION —
 *                           rejouer purgerait DÉFINITIVEMENT ce qui a été mis à la
 *                           corbeille DEPUIS l'échec, que personne n'avait demandé.
 *  - `dedup_apply`,
 *    `dedup_exact_apply`  : rejouent des DÉCISIONS de suppression prises contre une
 *                           analyse devenue périmée, dont une partie est déjà appliquée.
 *  - `originals_cleanup`  : efface les sauvegardes `_original` trouvées À L'EXÉCUTION —
 *                           y compris celles créées entre-temps (p.ex. par un `rotate`).
 *  - `empty_folders_cleanup` : même raison — la cible (les répertoires vides) est
 *                           RE-CALCULÉE à l'exécution, donc rejouer supprimerait aussi
 *                           les dossiers vidés DEPUIS l'échec, que personne n'avait vus
 *                           dans l'aperçu. Relancer l'outil depuis son écran redonne un
 *                           aperçu à jour : c'est le chemin sûr.
 *  - `exif_publish`,
 *    `exif_erase`,
 *    `move`, `compress`,
 *    `rename_extensions`  : écritures sur les fichiers ORIGINAUX. Elles n'ont pas de
 *                           reprise sûre à ce niveau, et l'utilisateur a un chemin
 *                           direct : refaire l'analyse et relancer l'outil depuis son
 *                           écran, en repartant de l'état RÉEL du disque.
 *  - `thumbnail`          : aucun handler n'est enregistré pour ce type (cf. `index.ts`) —
 *                           le « relancer » échouerait immédiatement.
 *
 * La bonne réponse pour un type refusé est un message clair, PAS un bouton dangereux :
 * la route renvoie 409 `JOB_NOT_RETRYABLE`.
 */
export const RETRYABLE_JOB_TYPES = ['scan', 'dedup', 'ai_tag'];
/** Ce type de job peut-il être ré-enfilé tel quel ? (source unique front + back). */
export function isRetryableJobType(type) {
    return RETRYABLE_JOB_TYPES.includes(type);
}
//# sourceMappingURL=job.js.map